import { useState, useEffect } from 'react';
import { Brain, Search, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

export default function MnemosynePage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('mnemosyne'); }, []);

  const [stats, setStats] = useState(null);
  const [memories, setMemories] = useState([]);
  const [filter, setFilter] = useState({ wing: '', room: '', type: '', limit: 30 });
  const [searchText, setSearchText] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const sRes = await fetch('/api/mnemosyne/stats');
        if (sRes.ok) setStats(await sRes.json());
      } catch {}
    }
    load();
  }, []);

  useEffect(() => { fetchMemories(); }, [filter]);

  async function fetchMemories() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.wing) params.set('wing', filter.wing);
      if (filter.room) params.set('room', filter.room);
      if (filter.type) params.set('type', filter.type);
      params.set('limit', String(filter.limit));
      const res = await fetch(`/api/mnemosyne/memories?${params}`);
      if (res.ok) setMemories(await res.json());
    } catch {} finally { setLoading(false); }
  }

  async function handleSearch() {
    if (!searchText.trim()) { fetchMemories(); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/mnemosyne/search', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: searchText, limit: 20 }),
      });
      if (res.ok) setMemories(await res.json());
    } catch {} finally { setLoading(false); }
  }

  const wings = [...new Set((stats?.byWing || []).map(w => w.wing))];
  const types = [...new Set((stats?.byType || []).map(t => t.memory_type))];

  const { impact, track, loading: trackingLoading } = usePageTracking('mnemosyne');
  const recentLessonsCount = (stats?.byType || []).find(t => t.memory_type === 'lesson')?.cnt || 0;
  const activePatternsCount = (stats?.byWing || []).find(w => w.wing === 'pattern')?.cnt || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Brain className="w-6 h-6 text-purple-400" />
        <h1 className="text-2xl font-bold text-white">Mnemosyne</h1>
        <span className="text-korinth-muted text-sm font-mono">MNEMOSYNE · Memory Keeper — KAIROS long-term memory</span>
      </div>

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Total memories:</span> <span className="text-white font-bold">{stats?.total || 0}</span></span>
        <span><span className="text-korinth-muted">Recent lessons:</span> <span className="text-korinth-accent font-bold">{recentLessonsCount}</span></span>
        <span><span className="text-korinth-muted">Active patterns:</span> <span className="text-korinth-accent font-bold">{activePatternsCount}</span></span>
        {(stats?.byWing || []).map(w => (
          <span key={w.wing}><span className="text-korinth-muted">{w.wing}:</span> <span className="text-korinth-text">{w.cnt}</span></span>
        ))}
      </div>

      {/* Sprint 20: Portfolio impact */}
      <PortfolioImpactPanel topic="Mnemosyne" {...impact} loading={trackingLoading} />

      {/* Search + filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px] flex gap-2">
          <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search memories..." className="flex-1 px-3 py-2 bg-korinth-bg border border-korinth-border rounded-lg text-xs font-mono text-white placeholder-korinth-muted/50 focus:border-purple-400 outline-none" />
          <button onClick={handleSearch} className="px-3 py-2 bg-purple-500/10 text-purple-400 border border-purple-500/30 rounded-lg text-xs font-mono hover:bg-purple-500/20">
            <Search className="w-3.5 h-3.5" />
          </button>
        </div>
        <select value={filter.wing} onChange={e => setFilter(f => ({ ...f, wing: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded-lg px-2 py-2 text-[10px] font-mono text-korinth-text">
          <option value="">All Wings</option>
          {wings.map(w => <option key={w} value={w}>{w}</option>)}
        </select>
        <select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded-lg px-2 py-2 text-[10px] font-mono text-korinth-text">
          <option value="">All Types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Memory list */}
      <ModuleContainer title={`Memories (${memories.length})`}>
        <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
          {loading ? (
            <p className="text-xs text-korinth-muted font-mono text-center py-8">Loading...</p>
          ) : memories.length > 0 ? memories.map((m, i) => (
            <div key={m.id || i} className="bg-korinth-bg rounded-lg border border-korinth-border/30">
              <button onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                className="w-full flex items-start gap-2 px-3 py-2 text-left">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">{m.wing}</span>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-korinth-border text-korinth-muted">{m.room}</span>
                    {m.drawer && <span className="text-[9px] font-mono text-korinth-muted/60">{m.drawer}</span>}
                    <span className="text-[8px] font-mono text-korinth-muted/40 ml-auto">{m.created_at?.slice(0, 16)}</span>
                  </div>
                  <p className="text-xs text-korinth-text">{expanded === m.id ? m.content : m.content?.slice(0, 120) + (m.content?.length > 120 ? '...' : '')}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[9px] font-mono text-korinth-muted">imp:{m.importance}</span>
                  {m.access_count > 0 && (
                    <span className="text-[8px] font-mono text-purple-400 flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{m.access_count}</span>
                  )}
                  {expanded === m.id ? <ChevronUp className="w-3 h-3 text-korinth-muted" /> : <ChevronDown className="w-3 h-3 text-korinth-muted" />}
                </div>
              </button>
              {expanded === m.id && m.context_json && (
                <div className="px-3 pb-2 border-t border-korinth-border/20 mt-1 pt-2">
                  <h4 className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Context</h4>
                  <pre className="text-[9px] font-mono text-korinth-muted bg-korinth-surface p-2 rounded overflow-x-auto max-h-32">
                    {JSON.stringify(m.context_json, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )) : (
            <p className="text-xs text-korinth-muted font-mono text-center py-8">No memories match filters</p>
          )}
        </div>
      </ModuleContainer>

      {/* Sprint 20: Track record */}
      <TrackRecordPanel {...track} loading={trackingLoading} metric_name="Mnemosyne" />
    </div>
  );
}
